// src/pages/GenericListPage.js

import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
// import PageContainer from '../components/common/PageContainer'; // This is no longer needed
import Modal from '../components/common/Modal';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import useApi from '../hooks/useApi';
import { format, isValid } from 'date-fns';

const GenericListPage = ({
  title,
  permission,
  entityEndpoint,
  columns,
  FormComponent,
  onRowClick,
  filterConfig = [],
  hideActions = false,
  headerActions,
  defaultFilters = {},
  onDownloadCsv = null, // <-- new prop
  rowActions = null,
}) => {
  const api = useApi();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  const initialFilters = {};
  filterConfig.forEach(filter => {
    initialFilters[filter.name] = filter.type === 'select' ? '' : '';
  });
  const [filters, setFilters] = useState(defaultFilters);
  const [filterDropdownData, setFilterDropdownData] = useState({});

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  useEffect(() => {
    const fetchDropdowns = async () => {
      const newDropdownData = {};
      for (const filter of filterConfig) {
        if (filter.type === 'select' && filter.endpoint) {
          try {
            const response = await api.get(filter.endpoint);
            newDropdownData[filter.name] = response.data || response;
          } catch (err) {
            console.error(`Failed to fetch data for filter '${filter.name}' from ${filter.endpoint}:`, err);
          }
        }
      }
      setFilterDropdownData(newDropdownData);
    };
    if (filterConfig.some(f => f.type === 'select')) {
      fetchDropdowns();
    }
  }, [api, filterConfig]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const allQueryParams = {
        search: debouncedSearchTerm,
        page: currentPage,
        limit: itemsPerPage,
      };

      Object.keys(filters).forEach(key => {
        const value = filters[key];
        if (value !== '' && value !== null) {
          const filterDef = filterConfig.find(f => f.name === key);
          if (filterDef && filterDef.type === 'date' && isValid(new Date(value))) {
            allQueryParams[key] = new Date(value).toISOString();
          } else {
            allQueryParams[key] = value;
          }
        }
      });
      
      const options = {
        queryParams: allQueryParams,
      };
      const response = await api.get(entityEndpoint, options);

      if (response && response.data && typeof response.totalCount === 'number') {
        setData(response.data);
        setTotalItems(response.totalCount);
      } else if (Array.isArray(response)) {
        setData(response);
        setTotalItems(response.length);
        console.warn("Backend response missing 'data' or 'totalCount'. Pagination may not function correctly.");
      } else {
        setData([]);
        setTotalItems(0);
      }

    } catch (err) {
      setError(err.message || `Failed to fetch ${title.toLowerCase()}.`);
      console.error(`Error fetching ${title.toLowerCase()}:`, err);
    } finally {
      setLoading(false);
    }
  }, [api, entityEndpoint, title, debouncedSearchTerm, currentPage, itemsPerPage, filters, filterConfig]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setFilters(initialFilters);
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  const handleDelete = async (id, nameIdentifier) => {
    if (!window.confirm(`Are you sure you want to delete ${nameIdentifier}? This action cannot be undone.`)) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await api.delete(`${entityEndpoint}/${id}`);
      fetchData();
      alert(`${nameIdentifier} deleted successfully!`);
    } catch (err) {
      setError(err.message || `Failed to delete ${nameIdentifier}.`);
      console.error(`Error deleting ${nameIdentifier}:`, err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setSelectedItem(null);
    setIsModalOpen(true);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  const handleSaveSuccess = () => {
    setIsModalOpen(false);
    fetchData();
  };

   const getSingularPropName = (pageTitle) => {
    // Handle specific irregular plurals
    if (pageTitle === 'Categories') return 'category';
    // For most regular plurals, remove 's'
    if (pageTitle.endsWith('s')) {
      return pageTitle.slice(0, -1).toLowerCase();
    }
    return pageTitle.toLowerCase(); // Return as is if not plural
  };

  const actionButton =
    FormComponent &&
    !hideActions && (
      <button
        onClick={handleAddNew}
        className="bg-indigo-600 text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg hover:bg-indigo-700 transition duration-200 text-sm sm:text-base"
      >
        Add New {title.slice(0, -1)}
      </button>
    );

  return (
    <Layout
      title={title}
      headerActions={headerActions}
      actionButton={actionButton}
    >
      {/* The content that was inside PageContainer is now here */}
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
        {/* Advanced Filters Section */}
        <div className="mb-6 bg-gray-50 p-4 rounded-lg shadow-inner">
          <h4 className="text-lg font-semibold mb-3 text-gray-700">Filters</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"> {/* Responsive grid */}
            {/* Search Input */}
            <div>
              <label htmlFor="search" className="block text-sm font-medium text-gray-700">Search</label>
              <input
                type="text"
                id="search"
                placeholder={`Search ${title.toLowerCase()}...`}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              />
            </div>

            {/* Dynamic Filter Inputs */}
            {filterConfig.map(filter => {
              const filterValue = filters[filter.name];
              const commonProps = {
                key: filter.name,
                id: filter.name,
                name: filter.name,
                className: "mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm", // Adjusted text size
                value: filterValue,
                onChange: (e) => handleFilterChange(filter.name, e.target.value),
              };

              switch (filter.type) {
                case 'select':
                  const options = filter.options || filterDropdownData[filter.name] || [];
                  return (
                    <div key={filter.name}>
                      <label htmlFor={filter.name} className="block text-sm font-medium text-gray-700">{filter.label}</label>
                      <select {...commonProps}>
                        <option value="">All {filter.label}s</option>
                        {options.map(option => (
                          <option key={option[filter.keyField || 'value']} value={option[filter.keyField || 'value']}>
                            {option[filter.valueField || 'label']}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                case 'date':
                  const dateValue = isValid(new Date(filterValue)) ? format(new Date(filterValue), 'yyyy-MM-dd') : '';
                  return (
                    <div key={filter.name}>
                      <label htmlFor={filter.name} className="block text-sm font-medium text-gray-700">{filter.label}</label>
                      <input
                        type="date"
                        {...commonProps}
                        value={dateValue}
                      />
                    </div>
                  );
                case 'number':
                  return (
                    <div key={filter.name}>
                      <label htmlFor={filter.name} className="block text-sm font-medium text-gray-700">{filter.label}</label>
                      <input
                        type="number"
                        {...commonProps}
                      />
                    </div>
                  );
                case 'text':
                default:
                  return (
                    <div key={filter.name}>
                      <label htmlFor={filter.name} className="block text-sm font-medium text-gray-700">{filter.label}</label>
                      <input
                        type="text"
                        {...commonProps}
                      />
                    </div>
                  );
              }
            })}
          </div>
          <div className="mt-4 flex justify-end">
            {onDownloadCsv && (
              <button
                onClick={() => onDownloadCsv({ search: debouncedSearchTerm, ...filters })}
                disabled={loading}
                className="mr-3 inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 transition duration-200 text-sm sm:text-base disabled:opacity-50"
                title="Download CSV (current filters)"
              >
                {/* simple download icon */}
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v12m0 0l-4-4m4 4l4-4M21 21H3" /></svg>
                Download CSV
              </button>
            )}
            <button
              onClick={handleClearFilters}
              className="px-3 py-1.5 sm:px-4 sm:py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 transition duration-200 text-sm sm:text-base"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {loading ? (
          <LoadingSpinner />
        ) : error ? (
          <ErrorMessage message={error} />
        ) : data.length === 0 ? (
          <p className="text-center text-gray-600 py-8">No {title.toLowerCase()} found matching your criteria.</p>
        ) : (
          // REVISED and SIMPLIFIED table container for reliable scrolling
          <div className="overflow-x-auto relative shadow-md sm:rounded-lg">
            <table className="w-full text-sm text-left text-gray-500">
              <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                <tr>
                  {columns.map((col, index) => (
                    <th key={index} scope="col" className="px-6 py-3">
                      {col.header}
                    </th>
                  ))}
                  {!hideActions && (
                    <th scope="col" className="px-6 py-3 text-center">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white">
                {data.map((item) => (
                  <tr
                    key={item._id}
                    className="border-b hover:bg-gray-50"
                    onClick={onRowClick ? () => onRowClick(item) : undefined}
                    style={onRowClick ? { cursor: 'pointer' } : {}}
                  >
                    {columns.map((col, index) => (
                      <td key={index} className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                        {col.render ? col.render(item) : item[col.key]}
                      </td>
                    ))}
                    {!hideActions && (
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center space-x-4">
                          {typeof rowActions === 'function' && rowActions(item, fetchData)}
                          {FormComponent && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleEdit(item); }}
                              className="text-indigo-600 hover:text-indigo-900"
                              title="Edit"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path></svg>
                            </button>
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDelete(item._id, item.name || `item ${item._id}`); }}
                            className="text-red-600 hover:text-red-900"
                            title="Delete"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row justify-center items-center space-y-2 sm:space-y-0 sm:space-x-2 mt-6"> {/* Responsive layout for pagination */}
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1 || loading}
              className="px-2 py-1 sm:px-3 sm:py-1 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              Previous
            </button>
            <div className="flex space-x-1">
              {pageNumbers.map((number) => (
                <button
                  key={number}
                  onClick={() => handlePageChange(number)}
                  disabled={loading}
                  className={`px-2 py-1 sm:px-3 sm:py-1 border rounded-md ${currentPage === number ? 'bg-indigo-600 text-white' : 'border-gray-300 text-gray-700 hover:bg-gray-100'} disabled:opacity-50 disabled:cursor-not-allowed text-sm`}
                >
                  {number}
                </button>
              ))}
            </div>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages || loading}
              className="px-2 py-1 sm:px-3 sm:py-1 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* The modal is kept outside the main content div */}
      {FormComponent && (
        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={
            selectedItem
              ? `Edit ${title.slice(0, -1)}`
              : `Add New ${title.slice(0, -1)}`
          }
        >
          <FormComponent
            {...(selectedItem && { [getSingularPropName(title)]: selectedItem })}
            onClose={() => setIsModalOpen(false)}
            onSaveSuccess={handleSaveSuccess}
          />
        </Modal>
      )}
    </Layout>
  );
};

export default GenericListPage;
